import withPagePermissionCheck from "../../../utils/withPagePermissionCheck";
import PermissionService from "../../../services/PermissionService";
import type { Component, VoidComponent } from "solid-js";
import { createMemo, createUniqueId, For, Match, Show, Switch } from "solid-js";
import type { RouteSectionProps } from "@solidjs/router";
import { useNavigate } from "@solidjs/router";
import { useCustomerState } from "../../../state/customer";
import ContentSection from "../../formEditor/ContentSection";
import Text from "../../../components/text/Text";
import Pill from "../../../components/pill/Pill";
import { useTranslation } from "../../../state/i18n";
import { Header } from "../../../components/inputs/Input";
import CopyValueButton from "./CopyValueButton";
import { IconButton, TextAndIconButton, TextButton } from "../../../components/button/Button";
import { useModalManager } from "../../../components/modal/ModalManager";
import DeleteWebhookModal from "./DeleteWebhookModal";
import UpsertWebhookModal from "./UpsertWebhookModal";
import ApiService from "../../../services/ApiService";
import { Breadcrumb } from "../../../components/breadcrumb/Breadcrumb";
import WebhookId from "../../../state/WebhookId";
import { Toggle } from "../../../components/toggle/Toggle";
import SeparatorLine from "../../../components/SeparatorLine";
import { CompactPasswordInput } from "../../../components/inputs/PasswordInput";
import { isEmpty, isNil } from "lodash-es";
import { NON_RETRYABLE_STATUS_CODES } from "../../../utils/apiUtil";
import { ResourceNotFoundContent } from "../../ResourceNotFoundPage";
import { is2xx, is4xx, is5xx } from "../../../utils/httpStatusUtil";
import withPageProductFeatureCheck from "../../../utils/withPageProductFeatureCheck";
import GenericFailureModal from "../../GenericFailureModal";
import GlobalTabs from "../../../components/tabs/GlobalTabs";
import FormatDate from "../../../components/formatting/FormatDate";
import DetailModal from "../../../components/modal/DetailModal";
import SimpleTable from "../../../components/table/SimpleTable";
import { Icon } from "../../../components/icon/Icon";
import { QueryBoundary } from "../../../utils/QueryBoundary";
import type { Webhook, WebhookInvocation } from "../../../types/Webhook";
import { keyFromSelector } from "i18next";

const WebhookPage: Component<RouteSectionProps<never>> = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { open, replace } = useModalManager();
  const customer = useCustomerState();
  const secretInputId = createUniqueId();

  const webhookQuery = createMemo(() => {
    const webhookId = WebhookId.from(props.params.webhookId!);
    return ApiService.getWebhook(webhookId);
  });
  const webhookInvocationsQuery = createMemo(() => {
    const webhookId = WebhookId.from(props.params.webhookId!);
    return ApiService.getWebhookInvocations(webhookId);
  });
  const updateWebhookMutation = ApiService.updateWebhook(WebhookId.from(props.params.webhookId!));

  return (
    <div class="flex h-full flex-col">
      <Show when={!isNil(webhookQuery().error) && NON_RETRYABLE_STATUS_CODES.includes(webhookQuery().error!.status)}>
        <ResourceNotFoundContent
          resource={keyFromSelector(($) => $.RESOURCE_WEBHOOK)}
          illustration={{ primaryIcon: "webhook" }}
        />
      </Show>

      <Show when={webhookQuery().data}>
        {(webhook) => (
          <>
            <div class="flex items-center border-b border-subtle pb-4">
              <div class="flex min-w-0 grow flex-col gap-2">
                <div>
                  <Breadcrumb
                    crumbs={[
                      {
                        label: t(($) => $.SETTINGS_WEBHOOKS_BREADCRUMB_BACK),
                        href: `/customers/${customer.customerId}/settings/webhooks`,
                      },
                    ]}
                  />
                </div>
                <Text class="text-header-xl text-primary" text={webhook().name} truncate />
              </div>

              <div class="flex gap-4">
                <Toggle
                  label={
                    webhook().enabled
                      ? t(($) => $.SETTINGS_WEBHOOKS_STATUS_ENABLED)
                      : t(($) => $.SETTINGS_WEBHOOKS_STATUS_DISABLED)
                  }
                  labelAlignment="right"
                  checked={webhook().enabled}
                  setChecked={(value) => {
                    updateWebhookMutation.mutate({
                      id: webhook().id.toString(),
                      name: webhook().name,
                      url: webhook().url,
                      status: value ? "ACTIVE" : "DISABLED",
                      type: webhook().type,
                    });
                  }}
                />

                <SeparatorLine orientation="vertical" />

                <div class="flex gap-2">
                  <TextAndIconButton
                    variant="secondary"
                    size="large"
                    text={t(($) => $.SETTINGS_WEBHOOKS_ACTIONS_EDIT)}
                    icon="pencil"
                    onClick={() => {
                      open({
                        content: () => (
                          <UpsertWebhookModal
                            webhook={webhook()}
                            title={t(($) => $.SETTINGS_WEBHOOKS_EDIT_MODAL_TITLE)}
                            confirmLabel={t(($) => $.SETTINGS_WEBHOOKS_EDIT_MODAL_ACTIONS_CONFIRM)}
                            onConfirm={({ name, url, type }) =>
                              new Promise((resolve, reject) => {
                                updateWebhookMutation.mutate(
                                  {
                                    id: webhook().id.toString(),
                                    name,
                                    url,
                                    type,
                                    status: webhook().enabled ? "ACTIVE" : "DISABLED",
                                  },
                                  {
                                    onSuccess: () => {
                                      resolve();
                                    },
                                    onError: (err) => {
                                      replace({
                                        content: () => <GenericFailureModal error={err} />,
                                      });
                                      reject(err);
                                    },
                                  },
                                );
                              })
                            }
                          />
                        ),
                      });
                    }}
                  />
                  <IconButton
                    variant="secondary"
                    size="large"
                    icon="trash"
                    aria-label={t(($) => $.DELETE_ARIA)}
                    onClick={() => {
                      open({
                        content: () => (
                          <DeleteWebhookModal
                            webhookId={webhook().id}
                            onConfirm={() => navigate(`/customers/${customer.customerId}/settings/webhooks`)}
                          />
                        ),
                      });
                    }}
                  />
                </div>
              </div>
            </div>

            <div class="flex h-full flex-col overflow-y-auto pt-8 pb-6">
              <ContentSection>
                <div class="flex flex-col gap-6">
                  <div class="flex flex-col">
                    <div class="flex items-center gap-2">
                      <Text class="text-base-semibold text-primary" text={webhook().name} truncate />
                      <Show
                        when={webhook().enabled}
                        fallback={<Pill variant="neutral" label={t(($) => $.SETTINGS_WEBHOOKS_STATUS_DISABLED)} />}
                      >
                        <Pill variant="success" label={t(($) => $.SETTINGS_WEBHOOKS_STATUS_ENABLED)} />
                      </Show>
                    </div>
                    <Text class="text-base-regular text-secondary" text={webhook().url} truncate />
                  </div>

                  <div class="flex flex-col gap-1">
                    <Header id={secretInputId} label={t(($) => $.SETTINGS_WEBHOOKS_SECRET_LABEL)} />
                    <div class="flex gap-1">
                      <CompactPasswordInput id={secretInputId} disabled value={webhook().secret} />
                      <CopyValueButton value={webhook().secret} />
                    </div>
                  </div>

                  <div class="flex flex-col gap-1">
                    <div class="text-base-semibold text-secondary">{t(($) => $.SETTINGS_WEBHOOKS_EVENTS_LABEL)}</div>
                    <div>
                      <ul class="flex flex-wrap gap-x-1">
                        <For each={webhook().type}>
                          {(type) => (
                            <li>
                              <Pill variant="neutral" label={type} truncate={false} />
                            </li>
                          )}
                        </For>
                      </ul>
                    </div>
                  </div>
                </div>
              </ContentSection>

              <div class="mt-8 flex flex-col gap-1">
                <p class="text-header-lg text-primary">{t(($) => $.SETTINGS_WEBHOOKS_INVOCATIONS_TITLE)}</p>
                <p class="text-medium-regular text-secondary">
                  {t(($) => $.SETTINGS_WEBHOOKS_INVOCATIONS_DESCRIPTION)}
                </p>
              </div>

              <div class="mt-4 grow" aria-busy={webhookInvocationsQuery().isFetching}>
                <Show
                  when={webhookInvocationsQuery().data}
                  fallback={
                    <div class="flex size-full flex-col items-center justify-center bg-surface-default opacity-75">
                      <Icon name="spinner" size="2xl" type="solid" class="animate-spin icon-secondary" />
                      <span class="text-medium-regular text-primary">
                        {t(($) => $.SETTINGS_WEBHOOKS_INVOCATIONS_TABLE_LOADING_TITLE)}
                      </span>
                    </div>
                  }
                >
                  {(webhookInvocations) => (
                    <div
                      role="table"
                      style={{
                        "--column-template": "120px minmax(120px, 1fr) minmax(120px, 1fr) minmax(120px, 1fr) 120px",
                      }}
                      class="flex size-full border-collapse flex-col"
                    >
                      <div role="rowgroup">
                        <div
                          role="row"
                          class="grid grid-cols-template gap-3 border-b border-subtle text-base-semibold text-secondary"
                        >
                          <div role="columnheader" class="py-1.5">
                            {t(($) => $.SETTINGS_WEBHOOKS_INVOCATIONS_TABLE_HEADER_STATUS)}
                          </div>
                          <div role="columnheader" class="py-1.5">
                            {t(($) => $.SETTINGS_WEBHOOKS_INVOCATIONS_TABLE_HEADER_TYPE)}
                          </div>
                          <div role="columnheader" class="py-1.5">
                            {t(($) => $.SETTINGS_WEBHOOKS_INVOCATIONS_TABLE_HEADER_INVOKED_ON)}
                          </div>
                          <div role="columnheader" class="py-1.5">
                            {t(($) => $.SETTINGS_WEBHOOKS_INVOCATIONS_TABLE_HEADER_RETRY_ON)}
                          </div>
                          <div role="columnheader" class="py-1.5">
                            {t(($) => $.SETTINGS_WEBHOOKS_INVOCATIONS_TABLE_HEADER_DETAILS)}
                          </div>
                        </div>
                      </div>
                      <Show
                        when={!isEmpty(webhookInvocations())}
                        fallback={
                          <div class="py-4 text-medium-regular text-secondary">
                            {t(($) => $.SETTINGS_WEBHOOKS_INVOCATIONS_TABLE_EMPTY_STATE_TITLE)}
                          </div>
                        }
                      >
                        <div role="rowgroup" class="h-full divide-y divide-subtle overflow-y-auto pb-6">
                          <For each={webhookInvocations()}>
                            {(webhookInvocation) => (
                              <div
                                role="row"
                                class="grid grid-cols-template items-center gap-3 overflow-hidden text-base-regular text-secondary"
                              >
                                <div role="cell" class="py-4">
                                  <Switch
                                    fallback={
                                      <Pill
                                        variant="neutral"
                                        label={t(($) => $.SETTINGS_WEBHOOKS_INVOCATIONS_STATUS_UNKNOWN)}
                                      />
                                    }
                                  >
                                    <Match when={webhookInvocation.status === "SUCCESS"}>
                                      <Pill
                                        variant="success"
                                        label={t(($) => $.SETTINGS_WEBHOOKS_INVOCATIONS_STATUS_SUCCESS)}
                                      />
                                    </Match>
                                    <Match when={webhookInvocation.status === "FAILED"}>
                                      <Pill
                                        variant="danger"
                                        label={t(($) => $.SETTINGS_WEBHOOKS_INVOCATIONS_STATUS_FAILED)}
                                      />
                                    </Match>
                                  </Switch>
                                </div>
                                <div role="cell" class="flex py-4">
                                  <Text text={webhookInvocation.type} truncate />
                                </div>
                                <div role="cell" class="py-4">
                                  <FormatDate value={webhookInvocation.invokedOn} />
                                </div>
                                <div role="cell" class="py-4">
                                  <Show when={webhookInvocation.retryOn} fallback="-">
                                    {(retryOn) => <FormatDate value={retryOn()} />}
                                  </Show>
                                </div>
                                <div role="cell" class="py-4">
                                  <TextButton
                                    variant="secondary"
                                    size="medium"
                                    text={t(($) => $.SETTINGS_WEBHOOKS_INVOCATIONS_TABLE_ACTIONS_VIEW)}
                                    onClick={() => {
                                      open({
                                        content: () => (
                                          <WebhookInvocationDetailModal
                                            webhook={webhook()}
                                            webhookInvocation={webhookInvocation}
                                          />
                                        ),
                                      });
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                          </For>
                        </div>
                      </Show>
                    </div>
                  )}
                </Show>
              </div>
            </div>
          </>
        )}
      </Show>
    </div>
  );
};

type WebhookInvocationDetailModalProps = {
  webhook: Webhook;
  webhookInvocation: WebhookInvocation;
};
const WebhookInvocationDetailModal: VoidComponent<WebhookInvocationDetailModalProps> = (props) => {
  const { t } = useTranslation();
  const httpStatusCodePillVariant = createMemo(() => {
    if (is2xx(props.webhookInvocation.httpStatusCode)) {
      return "success";
    } else if (is4xx(props.webhookInvocation.httpStatusCode) || is5xx(props.webhookInvocation.httpStatusCode)) {
      return "danger";
    } else {
      return "neutral";
    }
  });

  return (
    <DetailModal
      title={t(($) => $.SETTINGS_WEBHOOKS_INVOCATIONS_DETAIL_MODAL_TITLE)}
      description={props.webhook.url}
      content={() => (
        <div class="flex min-h-0 flex-col gap-4">
          <SimpleTable
            data={[
              {
                label: t(($) => $.SETTINGS_WEBHOOKS_INVOCATIONS_DETAIL_TABLE_HEADER_TYPE),
                value: props.webhookInvocation.type,
              },
              {
                label: t(($) => $.SETTINGS_WEBHOOKS_INVOCATIONS_DETAIL_TABLE_HEADER_HTTP_STATUS_CODE),
                value: (
                  <Pill
                    variant={httpStatusCodePillVariant()}
                    label={props.webhookInvocation.httpStatusCode.toString()}
                  />
                ),
              },
              {
                label: t(($) => $.SETTINGS_WEBHOOKS_INVOCATIONS_DETAIL_TABLE_HEADER_INVOKED_ON),
                value: <FormatDate value={props.webhookInvocation.invokedOn} />,
              },
              {
                label: t(($) => $.SETTINGS_WEBHOOKS_INVOCATIONS_DETAIL_TABLE_HEADER_RETRY_ON),
                value: (
                  <Show when={props.webhookInvocation.retryOn} fallback="-">
                    {(retryOn) => <FormatDate value={retryOn()} />}
                  </Show>
                ),
              },
            ]}
          />

          <GlobalTabs
            initialTab="request"
            items={[
              {
                key: "request",
                label: () => t(($) => $.SETTINGS_WEBHOOKS_INVOCATIONS_DETAIL_TABS_REQUEST),
                Slot: () => {
                  const eventQuery = ApiService.getEvent(props.webhookInvocation.eventId);

                  return (
                    <div class="mt-2.5">
                      <QueryBoundary>
                        <pre class="rounded-sm border border-default bg-surface-disabled p-2 font-sans text-base-regular break-all whitespace-pre-wrap text-primary">
                          <Show when={eventQuery.data}>{(event) => JSON.stringify(event().data, null, 2)}</Show>
                        </pre>
                      </QueryBoundary>
                    </div>
                  );
                },
              },
              {
                key: "response",
                label: () => t(($) => $.SETTINGS_WEBHOOKS_INVOCATIONS_DETAIL_TABS_RESPONSE),
                Slot: () => (
                  <div class="mt-2.5">
                    <div class="rounded-sm border border-default bg-surface-disabled p-2 text-base-regular text-primary">
                      {props.webhookInvocation.message}
                    </div>
                  </div>
                ),
              },
            ]}
          />
        </div>
      )}
    />
  );
};

const ProtectedWebhookPage = withPageProductFeatureCheck(
  withPagePermissionCheck(WebhookPage, PermissionService.canManageWebhooks),
  "WEBHOOKS",
);

export default ProtectedWebhookPage;
